import { useEffect, useRef } from 'react';
import { useKeyboardHeight, KEYBOARD_GAP_PX } from './useKeyboardHeight';

interface KeyboardLiftOptions {
  /** The floating, bottom-pinned elements (e.g. the search bar overlay) to lift above the keyboard */
  bars: () => Array<HTMLElement | null | undefined>;
  /** Optional scroll container; gets bottom padding while the keyboard is open so its last rows stay reachable */
  scroll?: () => HTMLElement | null | undefined;
}

/**
 * Lifts bottom-pinned bars above the on-screen keyboard — the same behaviour (and the same
 * KEYBOARD_GAP_PX gap) as the search bars in BookingsListModal, ContactsListModal, the Inbox list
 * and the conversation footer, for screens that don't have their own copy of that code.
 *
 * The native keyboard doesn't resize the screen, so without this a bar pinned to the bottom ends up
 * behind it. The lift is animated and removed again when the keyboard closes.
 */
export function useKeyboardLift({ bars, scroll }: KeyboardLiftOptions): void {
  const keyboardHeight = useKeyboardHeight();
  const latest = useRef({ bars, scroll });
  const current = useRef(0);
  const raf = useRef(0);

  useEffect(() => {
    latest.current = { bars, scroll };
  });

  useEffect(() => {
    const target = keyboardHeight > 0 ? keyboardHeight + KEYBOARD_GAP_PX : 0;

    const apply = (value: number) => {
      latest.current.bars().forEach((el) => {
        if (!el) return;
        if (value > 0) el.style.setProperty('bottom', `${value}px`, 'important');
        else el.style.removeProperty('bottom');
      });
    };

    // Room for the list under the bar while the keyboard is up
    const container = latest.current.scroll?.();
    if (container) {
      if (target > 0) container.style.paddingBottom = `${target + 60}px`;
      else container.style.removeProperty('padding-bottom');
    }

    if (raf.current) cancelAnimationFrame(raf.current);
    const start = current.current;
    const delta = target - start;
    if (Math.abs(delta) < 1) {
      current.current = target;
      apply(target);
      return;
    }

    const duration = target > start ? 220 : 170;
    const startedAt = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      current.current = t < 1 ? start + delta * eased : target;
      apply(current.current);
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);

    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [keyboardHeight]);

  // Leave nothing behind when the screen goes away
  useEffect(() => {
    const ref = latest;
    return () => {
      ref.current.bars().forEach((el) => el?.style.removeProperty('bottom'));
      ref.current.scroll?.()?.style.removeProperty('padding-bottom');
    };
  }, []);
}
